import { useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import { frameRates } from '@cartwhl/common/types';
import formatTime from '@cartwhl/common/utils/formatTime';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import LoadingBlock from '@cartwhl/ui/atoms/LoadingBlock';
import Slider from '@cartwhl/ui/atoms/Slider';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import Button from '@cartwhl/ui/input/Button';

import { animationPlayerStore, sceneSettingsStore } from 'stores';

import { formatFrames } from '../utils';

export function PlaybackSettings() {
  const { duration, currentFrame, disabled } = useSnapshot(
    animationPlayerStore.state,
  );
  const {
    playbackSettings: { frameRate, playbackSpeed },
  } = useSnapshot(sceneSettingsStore.state);

  const [showTime, setShowTime] = useState(false);

  const displayString = useMemo(() => {
    if (showTime) {
      const frameTime = 1 / frameRate;
      const timeDisplay = `${formatTime(currentFrame * frameTime)}/${formatTime(duration.seconds)}s`;
      return timeDisplay;
    }
    const frameDisplay = `${formatFrames(currentFrame)}/${formatFrames(duration.frames - 1)}`;
    return frameDisplay;
  }, [showTime, frameRate, currentFrame, duration.seconds, duration.frames]);

  return (
    <div className="flex h-full items-center gap-2 pl-2 lg:pl-0">
      {duration.frames !== 0 && (
        <button
          onClick={() => setShowTime(!showTime)}
          className="hidden cursor-pointer lg:inline-block"
        >
          <Text
            as="div"
            variant="studio/body/medium/sm"
            className="text-gray-60 dark:text-gray-45 mr-2 -ml-2 min-w-[63px] text-end transition-all duration-100"
            style={{
              width:
                displayString.split('/')[1]?.replace('.', '').toString()
                  .length * 20 || 80,
            }}
          >
            {displayString}
          </Text>
        </button>
      )}
      <Dropdown
        align="start"
        width={144}
        trigger={
          <Button variant="secondary" size="sm" disabled={disabled}>
            <span className="hidden md:inline">Frame rate:</span>
            <span className="md:ml-1">{frameRate}</span>
            <span className="inline md:hidden">fps</span>
          </Button>
        }
      >
        {frameRates.map((fr) => (
          <Dropdown.Item
            key={fr}
            icon={fr === frameRate ? <CheckmarkIcon /> : null}
            onClick={() => {
              console.log('clicked', fr);
              sceneSettingsStore.set('playbackSettings', 'frameRate', fr);
            }}
          >
            {fr}
          </Dropdown.Item>
        ))}
      </Dropdown>

      <Dropdown
        align="start"
        width={304}
        trigger={
          <Button
            variant="secondary"
            size="sm"
            className="hidden lg:inline-flex"
            disabled={disabled}
          >
            Playback: <span className="ml-1">{playbackSpeed}x</span>
          </Button>
        }
      >
        <div className="flex flex-col gap-2 px-2 py-4">
          <Text
            variant="studio/body/medium/sm"
            className="text-gray-60 dark:text-gray-45"
          >
            Playback speed:
          </Text>
          <div className="flex w-full gap-2">
            <Slider
              max={2}
              min={0.1}
              step={0.1}
              currentValue={playbackSpeed}
              onChange={(value) => {
                sceneSettingsStore.set(
                  'playbackSettings',
                  'playbackSpeed',
                  value,
                );
              }}
              trackColor="editor"
              rangeColor="blue"
              size="sm"
            />
            <Text
              as="div"
              variant="studio/body/medium/sm"
              className="bg-gray-15 dark:bg-gray-30 flex h-[26px] w-10 flex-none items-center justify-center rounded-lg px-2"
            >
              {playbackSpeed}
            </Text>
          </div>
        </div>
      </Dropdown>
    </div>
  );
}

export function MobilePlaybackSpeed() {
  const {
    playbackSettings: { playbackSpeed },
  } = useSnapshot(sceneSettingsStore.state);

  return (
    <Dropdown
      align="end"
      width={304}
      trigger={
        <Button variant="secondary" size="sm" className="md:hidden">
          @<span className="ml-1">{playbackSpeed}x</span>
        </Button>
      }
    >
      <div className="flex flex-col gap-2 px-2 py-4">
        <Text
          variant="studio/body/medium/sm"
          className="text-gray-60 dark:text-gray-45"
        >
          Playback speed:
        </Text>
        <div className="flex w-full gap-2">
          <Slider
            max={2}
            min={0.1}
            step={0.1}
            currentValue={playbackSpeed}
            onChange={(value) => {
              sceneSettingsStore.set(
                'playbackSettings',
                'playbackSpeed',
                value,
              );
            }}
            trackColor="editor"
            rangeColor="blue"
            size="sm"
          />
          <Text
            as="div"
            variant="studio/body/medium/sm"
            className="bg-gray-15 dark:bg-gray-30 flex h-[26px] w-10 flex-none items-center justify-center rounded-lg px-2"
          >
            {playbackSpeed}x
          </Text>
        </div>
      </div>
    </Dropdown>
  );
}
